import { useState, useRef } from 'react';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { UserIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const ProfilePhotoUpload = ({ currentPhoto, onUpload, userName = 'User' }) => {
  const [preview, setPreview] = useState(currentPhoto || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      // Call the upload callback
      await onUpload(formData);

      toast.success('Profile photo updated successfully');
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error('Upload error:', error);
      setPreview(currentPhoto); // Revert to current photo
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPreview(null);

    try {
      await onUpload(null); // Pass null to indicate removal
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
      setPreview(currentPhoto); // Revert
    }
  };

  const getInitials = () => {
    return userName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Photo Preview */}
      <div className="relative group">
        <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-primary-600 to-primary-400 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
          {preview ? (
            <img
              src={preview}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{getInitials()}</span>
          )}
        </div>

        {/* Upload Overlay */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        >
          <CameraIcon className="w-8 h-8 text-white" />
        </div>

        {/* Remove Button */}
        {preview && (
          <button
            onClick={handleRemovePhoto}
            className="absolute top-0 right-0 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
            title="Remove photo"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}

        {/* Loading Indicator */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <div className="spinner w-8 h-8" />
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Button */}
      <div className="flex flex-col items-center gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
        >
          <CameraIcon className="w-5 h-5" />
          {preview ? 'Change Photo' : 'Upload Photo'}
        </button>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          JPG, PNG or GIF (max. 5MB)
        </p>
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
